import { z } from 'zod'
import { idSchema, reasonSchema, revisionSchema, text } from '../validators'
import { REVIEW_STATES, type ReviewState } from '../vocab'
import type { PersonRef, PersonView } from './review'

/**
 * Meeting minutes (P-CAL-002, CAL-040..053): versioned, reviewed by the case's
 * independent reviewer, immutable once published; corrections are new versions.
 * Submission, approval and publication reasons are kept separately.
 */

/** Field limits (CAL-041), shared with the web editor (GAP-UI-016). */
export const MINUTES_LIMITS = {
  attendees: { min: 3, max: 2000 },
  discussion: { min: 8, max: 8000 },
  company_response: { min: 8, max: 6000 },
  fruuit_view: { min: 8, max: 6000 },
  agreed_actions: { min: 8, max: 6000 },
  limitations: { min: 8, max: 4000 },
  note: { max: 2000 },
} as const

export const MINUTES_CONTENT_FIELDS = [
  'attendees',
  'discussion',
  'company_response',
  'fruuit_view',
  'agreed_actions',
  'limitations',
] as const
export type MinutesContentField = (typeof MINUTES_CONTENT_FIELDS)[number]

const content = {
  attendees: text(MINUTES_LIMITS.attendees.min, MINUTES_LIMITS.attendees.max),
  discussion: text(MINUTES_LIMITS.discussion.min, MINUTES_LIMITS.discussion.max),
  company_response: text(MINUTES_LIMITS.company_response.min, MINUTES_LIMITS.company_response.max),
  fruuit_view: text(MINUTES_LIMITS.fruuit_view.min, MINUTES_LIMITS.fruuit_view.max),
  agreed_actions: text(MINUTES_LIMITS.agreed_actions.min, MINUTES_LIMITS.agreed_actions.max),
  limitations: text(MINUTES_LIMITS.limitations.min, MINUTES_LIMITS.limitations.max),
}

/** `POST /minutes`: the first version for a published meeting (CAL-040). */
export const minutesCreateSchema = z.strictObject({ meeting_id: idSchema, ...content })
export type MinutesCreate = z.infer<typeof minutesCreateSchema>

/** `PATCH /minutes/:id`: the author edits a draft or returned version (CAL-045). */
export const minutesUpdateSchema = z
  .strictObject({
    revision: revisionSchema,
    attendees: content.attendees.optional(),
    discussion: content.discussion.optional(),
    company_response: content.company_response.optional(),
    fruuit_view: content.fruuit_view.optional(),
    agreed_actions: content.agreed_actions.optional(),
    limitations: content.limitations.optional(),
  })
  .refine((v) => Object.keys(v).some((k) => k !== 'revision'), {
    message: 'Change at least one field.',
  })
export type MinutesUpdate = z.infer<typeof minutesUpdateSchema>

/** Submit: an optional note, stored as `submission_note` (CAL-046 CHANGE). */
export const minutesSubmitSchema = z.strictObject({
  revision: revisionSchema,
  note: z.string().max(MINUTES_LIMITS.note.max).optional(),
})
export type MinutesSubmit = z.infer<typeof minutesSubmitSchema>

/** Approve, publish and return each need their own substantive reason (WF-006). */
export const minutesReviewSchema = z.strictObject({
  revision: revisionSchema,
  reason: reasonSchema,
})
export type MinutesReview = z.infer<typeof minutesReviewSchema>

/**
 * `POST /minutes/:id/correct` on the latest published version: a new draft
 * version chained by `previous_id`, starting from the published content (CAL-043).
 */
export const minutesCorrectSchema = z.strictObject({
  change_reason: reasonSchema,
  attendees: content.attendees.optional(),
  discussion: content.discussion.optional(),
  company_response: content.company_response.optional(),
  fruuit_view: content.fruuit_view.optional(),
  agreed_actions: content.agreed_actions.optional(),
  limitations: content.limitations.optional(),
})
export type MinutesCorrect = z.infer<typeof minutesCorrectSchema>

export const minutesListQuerySchema = z.object({
  meeting_id: idSchema.optional(),
  state: z.enum(REVIEW_STATES.values).optional(),
  view: z.enum(['investor', 'staff']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(1024).optional(),
})
export type MinutesListQuery = z.infer<typeof minutesListQuerySchema>

/** One minutes version. Investors get the allowlisted fields only (P-ID-006, CAL-009). */
export interface MinutesVersion {
  id: string
  meeting_id: string
  engagement_id: string
  version: number
  previous_id: string | null
  state: ReviewState
  attendees: string
  discussion: string
  company_response: string
  fruuit_view: string
  agreed_actions: string
  limitations: string
  /** The latest published version is authoritative (CAL-051). */
  is_current: boolean
  superseded_by_version: number | null
  /** Display name only for investors; staff also get the user id (P-ID-006). */
  approved_by: PersonView | null
  approved_at: string | null
  published_by: PersonView | null
  published_at: string | null
  /** Staff only from here. */
  author?: PersonRef | null
  change_reason?: string | null
  submitted_at?: string | null
  submission_note?: string | null
  approval_reason?: string | null
  publication_reason?: string | null
  returned_at?: string | null
  return_reason?: string | null
  revision?: number
  created_at?: string
  updated_at?: string
  permissions?: {
    edit: boolean
    submit: boolean
    approve: boolean
    publish: boolean
    return: boolean
    correct: boolean
  }
}
